import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import api from '../lib/api';

/**
 * Admin/HR page for editing the evaluation-module configuration.
 * Backing endpoint: GET/PATCH /api/evaluations/config (admin-only PATCH).
 *
 * Sections (each is dirty-tracked separately so partial saves are supported):
 *   • Rating bands
 *   • Section floors + critical sections + item floor
 *   • Comment triggers
 *   • Reminder cadence + recipient rules (recipients are code-defined for v1)
 *   • Annual review window + merge window
 *   • 1-5 Rating scale reference copy (verbatim, editable rich-text)
 */
export default function EvaluationSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/evaluations/config');
      setCfg(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load evaluation config');
    } finally { setLoading(false); }
  };

  const patch = async (partial) => {
    setSaving(true);
    try {
      const { data } = await api.patch('/evaluations/config', partial);
      setCfg(data);
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return <div className="p-6 text-slate-500">Loading evaluation configuration…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="eval-settings-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Evaluation Settings</h1>
        <p className="text-sm text-slate-600 mt-1">
          Admin/HR-only. Every threshold in the evaluation module can be adjusted here without
          a code deploy. Changes take effect on the next evaluation save.
        </p>
      </div>

      <BandsCard cfg={cfg} onSave={patch} saving={saving} />
      <FloorsCard cfg={cfg} onSave={patch} saving={saving} />
      <CommentsCard cfg={cfg} onSave={patch} saving={saving} />
      <RemindersCard cfg={cfg} onSave={patch} saving={saving} />
      <DueVisibilityCard cfg={cfg} onSave={patch} saving={saving} />
      <AnnualWindowCard cfg={cfg} onSave={patch} saving={saving} />
      <RatingScaleCard cfg={cfg} onSave={patch} saving={saving} />
      <CredentialsCard cfg={cfg} onSave={patch} saving={saving} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Bands
// ──────────────────────────────────────────────────────────────────────
function BandsCard({ cfg, onSave, saving }) {
  const [rows, setRows] = useState(() => cfg.bands.map((b, i) => ({ ...b, _i: i })));
  const dirty = JSON.stringify(rows.map(({ _i, ...b }) => b)) !== JSON.stringify(cfg.bands);
  const setRow = (i, patch) => setRows(rows.map(r => r._i === i ? { ...r, ...patch } : r));

  return (
    <Card data-testid="bands-card">
      <CardHeader>
        <CardTitle>Rating Bands</CardTitle>
        <CardDescription>
          Overall-score → label mapping. Ranges are half-open <code>[min, max_excl)</code>. Order
          them from highest to lowest.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-[1fr_100px_100px_120px] gap-2 items-center text-xs font-semibold text-slate-600">
          <div>Label</div><div>Min ≥</div><div>Max &lt;</div><div>Meets standards</div>
        </div>
        {rows.map((b) => (
          <div key={b._i} className="grid grid-cols-[1fr_100px_100px_120px] gap-2 items-center">
            <Input value={b.label} onChange={e => setRow(b._i, { label: e.target.value })} data-testid={`band-label-${b._i}`} />
            <Input type="number" step="0.01" value={b.min} onChange={e => setRow(b._i, { min: parseFloat(e.target.value) })} data-testid={`band-min-${b._i}`} />
            <Input type="number" step="0.01" value={b.max_excl} onChange={e => setRow(b._i, { max_excl: parseFloat(e.target.value) })} data-testid={`band-max-${b._i}`} />
            <div className="flex justify-center">
              <Switch checked={!!b.meets_standards} onCheckedChange={v => setRow(b._i, { meets_standards: v })} data-testid={`band-meets-${b._i}`} />
            </div>
          </div>
        ))}
        <div className="flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => onSave({ bands: rows.map(({ _i, ...b }) => b) })}
            data-testid="save-bands-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save bands
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Section floors + critical sections + item floor
// ──────────────────────────────────────────────────────────────────────
function FloorsCard({ cfg, onSave, saving }) {
  const [sf, setSf] = useState(cfg.section_floor);
  const [csf, setCsf] = useState(cfg.critical_section_floor);
  const [crit, setCrit] = useState((cfg.critical_sections || []).join(', '));
  const [itemFloor, setItemFloor] = useState(cfg.item_floor_rating);
  const dirty = sf !== cfg.section_floor
    || csf !== cfg.critical_section_floor
    || crit !== (cfg.critical_sections || []).join(', ')
    || itemFloor !== cfg.item_floor_rating;

  return (
    <Card data-testid="floors-card">
      <CardHeader>
        <CardTitle>Score Floors &amp; Critical Sections</CardTitle>
        <CardDescription>
          If any section average falls below the floor, the overall label is capped at
          Needs Improvement. Sections you mark as Critical get the stricter floor. The item
          floor triggers an HR-review warning when any single item is rated at/below that value.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Section floor (avg &lt;)</Label>
            <Input type="number" step="0.01" value={sf} onChange={e => setSf(parseFloat(e.target.value))} data-testid="section-floor" />
          </div>
          <div>
            <Label>Critical section floor (avg &lt;)</Label>
            <Input type="number" step="0.01" value={csf} onChange={e => setCsf(parseFloat(e.target.value))} data-testid="critical-section-floor" />
          </div>
          <div className="md:col-span-2">
            <Label>Critical sections (comma-separated letters, e.g. <code>B, E</code>)</Label>
            <Input value={crit} onChange={e => setCrit(e.target.value)} data-testid="critical-sections" />
          </div>
          <div>
            <Label>Item floor rating</Label>
            <Input type="number" min={1} max={5} value={itemFloor} onChange={e => setItemFloor(parseInt(e.target.value, 10))} data-testid="item-floor" />
            <p className="text-xs text-slate-500 mt-1">Any single item ≤ this triggers an HR-review flag on the evaluation.</p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => onSave({
              section_floor: sf,
              critical_section_floor: csf,
              critical_sections: crit.split(',').map(s => s.trim()).filter(Boolean),
              item_floor_rating: itemFloor,
            })}
            data-testid="save-floors-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save floors
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Comment triggers
// ──────────────────────────────────────────────────────────────────────
function CommentsCard({ cfg, onSave, saving }) {
  const [triggers, setTriggers] = useState((cfg.comment_required_ratings || []).join(', '));
  const dirty = triggers !== (cfg.comment_required_ratings || []).join(', ');

  return (
    <Card data-testid="comments-card">
      <CardHeader>
        <CardTitle>Comment Requirements</CardTitle>
        <CardDescription>
          Rating values that require a written comment before the evaluation can be saved.
          Managers cannot submit an evaluation with any listed rating that has no comment.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Rating values requiring comments (comma-separated, e.g. <code>1, 2</code>)</Label>
          <Input value={triggers} onChange={e => setTriggers(e.target.value)} data-testid="comment-triggers" />
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => onSave({
              comment_required_ratings: triggers.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n)),
            })}
            data-testid="save-comments-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Reminders
// ──────────────────────────────────────────────────────────────────────
function RemindersCard({ cfg, onSave, saving }) {
  const [days, setDays] = useState((cfg.reminder_days_before || []).join(', '));
  const dirty = days !== (cfg.reminder_days_before || []).join(', ');

  return (
    <Card data-testid="reminders-card">
      <CardHeader>
        <CardTitle>Reminder Cadence</CardTitle>
        <CardDescription>
          Days before a hire-date-driven evaluation target (90-Day Trial, 6-Month Review) that email
          reminders are sent to the employee's direct supervisor, reviewer, and manager. Once the
          evaluation is signed, reminders stop automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Days before target (comma-separated, e.g. <code>14, 7</code>)</Label>
          <Input value={days} onChange={e => setDays(e.target.value)} data-testid="reminder-days" />
          <p className="text-xs text-slate-500 mt-1">
            Overdue reminders (up to 3 days after target) always run and are not editable.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => onSave({
              reminder_days_before: days.split(',').map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n) && n > 0),
            })}
            data-testid="save-reminders-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// On-screen visibility of hire-date evals (My Work / HR completion)
// ──────────────────────────────────────────────────────────────────────
function DueVisibilityCard({ cfg, onSave, saving }) {
  const [ahead, setAhead] = useState(cfg.due_lookahead_days ?? 30);
  const [reach, setReach] = useState(cfg.due_overdue_reach_days ?? 90);
  const [start, setStart] = useState(cfg.eval_program_start_date || '');
  const dirty = Number(ahead) !== (cfg.due_lookahead_days ?? 30)
    || Number(reach) !== (cfg.due_overdue_reach_days ?? 90)
    || start !== (cfg.eval_program_start_date || '');

  return (
    <Card data-testid="due-visibility-card">
      <CardHeader>
        <CardTitle>Hire-Date Evaluations in My Work</CardTitle>
        <CardDescription>
          Controls what appears on screen — the reminder emails above are unaffected, so widening
          these windows never sends a wave of catch-up email.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Show upcoming evaluations this many days ahead</Label>
            <Input type="number" min="1" value={ahead} onChange={e => setAhead(e.target.value)}
                   data-testid="due-lookahead-days" />
          </div>
          <div>
            <Label>Keep overdue evaluations listed for this many days</Label>
            <Input type="number" min="0" value={reach} onChange={e => setReach(e.target.value)}
                   data-testid="due-overdue-reach-days" />
            <p className="text-xs text-slate-500 mt-1">
              Applies to employees hired before the program start date below.
            </p>
          </div>
        </div>
        <div>
          <Label>Evaluation program start date (optional)</Label>
          <Input type="date" value={start} onChange={e => setStart(e.target.value)}
                 data-testid="eval-program-start-date" />
          <p className="text-xs text-slate-500 mt-1">
            Anyone hired on or after this date stays in My Work until their 90-Day and 6-Month are
            done, however late — so a go-live backlog is never silently dropped. Employees hired
            before it are still limited by the overdue window above, which keeps long-tenured
            officers from filling the list. Leave blank to apply that limit to everyone.
          </p>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => onSave({
              due_lookahead_days: parseInt(ahead, 10) || 30,
              due_overdue_reach_days: parseInt(reach, 10) || 0,
              eval_program_start_date: start || null,
            })}
            data-testid="save-due-visibility-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Annual window + merge window
// ──────────────────────────────────────────────────────────────────────
function AnnualWindowCard({ cfg, onSave, saving }) {
  const aw = cfg.annual_window || {};
  const [sm, setSm] = useState(aw.start_month);
  const [sd, setSd] = useState(aw.start_day);
  const [em, setEm] = useState(aw.end_month);
  const [ed, setEd] = useState(aw.end_day);
  const [mw, setMw] = useState(cfg.merge_window_days);
  const dirty = sm !== aw.start_month || sd !== aw.start_day || em !== aw.end_month || ed !== aw.end_day || mw !== cfg.merge_window_days;

  return (
    <Card data-testid="annual-window-card">
      <CardHeader>
        <CardTitle>Annual Review Window &amp; Merge Rules</CardTitle>
        <CardDescription>
          Calendar-year window when annual evaluations run. If a 90-day or 6-month target falls
          within ±&#123;merge window&#125; days of this range, the standalone is auto-closed and
          merged into the annual.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label>Start month</Label><Input type="number" min={1} max={12} value={sm} onChange={e => setSm(parseInt(e.target.value, 10))} data-testid="aw-start-month" /></div>
          <div><Label>Start day</Label><Input type="number" min={1} max={31} value={sd} onChange={e => setSd(parseInt(e.target.value, 10))} data-testid="aw-start-day" /></div>
          <div><Label>End month</Label><Input type="number" min={1} max={12} value={em} onChange={e => setEm(parseInt(e.target.value, 10))} data-testid="aw-end-month" /></div>
          <div><Label>End day</Label><Input type="number" min={1} max={31} value={ed} onChange={e => setEd(parseInt(e.target.value, 10))} data-testid="aw-end-day" /></div>
        </div>
        <div className="max-w-xs">
          <Label>Merge window (± days)</Label>
          <Input type="number" min={0} value={mw} onChange={e => setMw(parseInt(e.target.value, 10))} data-testid="merge-window" />
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => onSave({
              annual_window: { start_month: sm, start_day: sd, end_month: em, end_day: ed },
              merge_window_days: mw,
            })}
            data-testid="save-annual-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────
// 1-5 Rating scale reference copy (editable)
// ──────────────────────────────────────────────────────────────────────
function CredentialsCard({ cfg, onSave, saving }) {
  const [enabled, setEnabled] = useState(!!cfg.credentials_enabled);
  const [headsup, setHeadsup] = useState(cfg.credential_headsup_email_enabled !== false);
  const [windowDays, setWindowDays] = useState(cfg.credential_return_window_days ?? 7);
  const [items, setItems] = useState(() => (cfg.credential_items || []).map((it, i) => ({ ...it, _i: i })));
  const base = { credentials_enabled: !!cfg.credentials_enabled, credential_headsup_email_enabled: cfg.credential_headsup_email_enabled !== false, credential_return_window_days: cfg.credential_return_window_days ?? 7, credential_items: cfg.credential_items || [] };
  const clean = items.map(({ _i, ...it }) => it);
  const dirty = enabled !== base.credentials_enabled || headsup !== base.credential_headsup_email_enabled
    || Number(windowDays) !== base.credential_return_window_days
    || JSON.stringify(clean) !== JSON.stringify(base.credential_items);
  const toggleItem = (i, v) => setItems(items.map(it => it._i === i ? { ...it, enabled: v } : it));

  return (
    <Card data-testid="credentials-card">
      <CardHeader>
        <CardTitle>Credentials</CardTitle>
        <CardDescription>
          Ask employees to provide license documents before they sign an Annual or 90-Day evaluation.
          When on, a heads-up email lists the documents when the supervisor submits the evaluation, and the employee
          uploads them on their signing page. Driver's License accepts a Driver's License or State ID;
          Security License accepts Armed, Unarmed or PI; the last firearms qualification is requested only for Armed officers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label>Require credential documents (master switch)</Label>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="cred-master-switch" />
        </div>
        <div className="flex items-center justify-between">
          <Label>Send heads-up email when the supervisor submits</Label>
          <Switch checked={headsup} onCheckedChange={setHeadsup} data-testid="cred-headsup-switch" />
        </div>
        <div className="flex items-center justify-between max-w-xs">
          <Label>Return window (days to provide)</Label>
          <Input type="number" min={1} max={60} value={windowDays}
                 onChange={e => setWindowDays(e.target.value)} className="w-24 text-center"
                 data-testid="cred-window-days" />
        </div>
        <div className="space-y-2 border-t pt-3">
          <p className="text-sm font-medium text-slate-700">Documents requested</p>
          {items.map((it) => (
            <div key={it._i} className="flex items-center justify-between">
              <Label className="font-normal">
                {it.label}{it.armed_only ? ' (Armed officers only)' : ''}
              </Label>
              <Switch checked={!!it.enabled} onCheckedChange={(v) => toggleItem(it._i, v)}
                      data-testid={`cred-item-${it.key}`} />
            </div>
          ))}
        </div>
        <Button disabled={!dirty || saving}
                onClick={() => onSave({ credentials_enabled: enabled, credential_headsup_email_enabled: headsup, credential_return_window_days: Math.max(1, Number(windowDays) || 7), credential_items: clean })}
                data-testid="cred-save">
          <Save className="w-4 h-4 mr-2" /> Save Credentials
        </Button>
      </CardContent>
    </Card>
  );
}

function RatingScaleCard({ cfg, onSave, saving }) {
  const [intro, setIntro] = useState(cfg.rating_scale_intro || '');
  const [levels, setLevels] = useState(() => (cfg.rating_scale_levels || []).map((l, i) => ({ ...l, _i: i })));
  const dirty = intro !== (cfg.rating_scale_intro || '')
    || JSON.stringify(levels.map(({ _i, ...l }) => l)) !== JSON.stringify(cfg.rating_scale_levels || []);
  const setLevel = (i, patch) => setLevels(levels.map(l => l._i === i ? { ...l, ...patch } : l));
  const addLevel = () => setLevels([...levels, { _i: Date.now(), value: null, label: '', description: '' }]);
  const removeLevel = (i) => setLevels(levels.filter(l => l._i !== i));

  return (
    <Card data-testid="rating-scale-card">
      <CardHeader>
        <CardTitle>1–5 Rating Scale Reference</CardTitle>
        <CardDescription>
          Copy shown in the collapsible reference panel on every evaluation and appended to
          every finalized PDF. Editing here updates both in-app and PDF references immediately.
          <span className="block mt-1 text-xs text-slate-500">
            <Info className="inline w-3 h-3 mr-1 -mt-0.5" />
            To leave the value column blank for the N/A row, enter <code>-</code> or leave it empty.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Intro paragraph</Label>
          <Textarea rows={5} value={intro} onChange={e => setIntro(e.target.value)} data-testid="scale-intro" />
        </div>
        <div className="space-y-3">
          {levels.map((lv) => (
            <div key={lv._i} className="grid grid-cols-[60px_180px_1fr_40px] gap-2 items-start">
              <Input
                value={lv.value == null ? '' : lv.value}
                onChange={e => {
                  const v = e.target.value.trim();
                  setLevel(lv._i, { value: (v === '' || v === '-') ? null : parseInt(v, 10) });
                }}
                placeholder="—"
                data-testid={`scale-value-${lv._i}`}
                className="text-center"
              />
              <Input value={lv.label} onChange={e => setLevel(lv._i, { label: e.target.value })} data-testid={`scale-label-${lv._i}`} placeholder="Label" />
              <Textarea rows={2} value={lv.description} onChange={e => setLevel(lv._i, { description: e.target.value })} data-testid={`scale-desc-${lv._i}`} placeholder="Description" />
              <Button variant="ghost" size="icon" onClick={() => removeLevel(lv._i)} data-testid={`scale-remove-${lv._i}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addLevel} data-testid="scale-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add level
          </Button>
        </div>
        <div className="flex justify-end">
          <Button
            disabled={!dirty || saving}
            onClick={() => onSave({
              rating_scale_intro: intro,
              rating_scale_levels: levels.map(({ _i, ...l }) => l),
            })}
            data-testid="save-scale-btn"
          >
            <Save className="w-4 h-4 mr-1" /> Save rating scale
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
